// skipcq: JS-W1028
/* eslint-disable react-hooks/set-state-in-effect -- clears cached runs when the source list empties */
import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import StickyButtonBar from '../../components/common/StickyButtonBar';
import useBreweryBeerSources, { fetchLatestIngestionRun } from '../../hooks/useBreweryBeerSources';
import useBreweryFindBeerFlow from '../../hooks/useBreweryFindBeerFlow';
import BreweryBeerSourceFormDialog from './BreweryBeerSourceFormDialog';
import {
  BeerSourceListPanel,
  formatBeerSourceLabel,
} from './breweryBeerSourcePanels';
import { RecreateMonitorDialog } from '../beerIngestion/beerIngestionPanels';
import {
  BREWERY_SOURCE_FILTER_ALL,
  filterBreweryBeerSources,
  MONITORING_STATUSES,
  sortBreweryBeerSourcesByUrl,
  SOURCE_CATEGORIES,
  SOURCE_STRUCTURES,
} from '../../utils/brewery/breweryBeerSourceFilters';

function BreweryBeerSourceFilterSelect({
  filterId,
  label,
  value,
  options,
  minWidth,
  allLabel = 'All',
  extraOptions = [],
  formatOptionLabel,
  onChange,
}) {
  const formatLabel = formatOptionLabel ?? formatBeerSourceLabel;
  return (
    <FormControl size="small" sx={{ minWidth }}>
      <InputLabel id={`${filterId}-label`}>{label}</InputLabel>
      <Select
        labelId={`${filterId}-label`}
        id={filterId}
        value={value}
        label={label}
        onChange={onChange}
      >
        <MenuItem value={BREWERY_SOURCE_FILTER_ALL}>{allLabel}</MenuItem>
        {extraOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {formatLabel(option)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function SourcesFilterBar({
  sourceStructureFilter,
  sourceCategoryFilter,
  monitoringStatusFilter,
  onSourceStructureChange,
  onSourceCategoryChange,
  onMonitoringStatusChange,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap',
        alignItems: 'center',
        mb: 2,
      }}
    >
      <BreweryBeerSourceFilterSelect
        filterId="monitoring-status-filter"
        label="Status"
        value={monitoringStatusFilter}
        options={MONITORING_STATUSES}
        minWidth={140}
        onChange={onMonitoringStatusChange}
      />
      <BreweryBeerSourceFilterSelect
        filterId="source-category-filter"
        label="Category"
        value={sourceCategoryFilter}
        options={SOURCE_CATEGORIES}
        minWidth={160}
        onChange={onSourceCategoryChange}
      />
      <BreweryBeerSourceFilterSelect
        filterId="source-structure-filter"
        label="Source structure"
        value={sourceStructureFilter}
        options={SOURCE_STRUCTURES}
        minWidth={180}
        onChange={onSourceStructureChange}
      />
    </Box>
  );
}

function BreweryBeerSourcesSection({
  parentBreweryId,
  breweryName,
  breweryCity,
  breweryState,
}) {
  const {
    sources,
    loading,
    error,
    actionError,
    actionLoading,
    pendingAction,
    addOrUpdateSource,
    markTrusted,
    updateStatus,
    checkNow,
    recreateMonitor,
  } = useBreweryBeerSources(parentBreweryId);

  const [sourceStructureFilter, setSourceStructureFilter] = useState(BREWERY_SOURCE_FILTER_ALL);
  const [sourceCategoryFilter, setSourceCategoryFilter] = useState(BREWERY_SOURCE_FILTER_ALL);
  const [monitoringStatusFilter, setMonitoringStatusFilter] = useState('active');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [scrapingSourceId, setScrapingSourceId] = useState(null);
  const [recreateSource, setRecreateSource] = useState(null);
  const [latestRunsBySourceId, setLatestRunsBySourceId] = useState({});

  const { handleFindBeer, openFindBeerWithUrl, findBeerDialog } = useBreweryFindBeerFlow({
    parentBreweryId,
    breweryName,
    breweryCity,
    breweryState,
  });

  const filteredSources = useMemo(
    () =>
      sortBreweryBeerSourcesByUrl(
        filterBreweryBeerSources(sources, {
          sourceStructureFilter,
          sourceCategoryFilter,
          monitoringStatusFilter,
        }),
      ),
    [sources, sourceStructureFilter, sourceCategoryFilter, monitoringStatusFilter],
  );

  useEffect(() => {
    if (sources.length === 0) {
      setLatestRunsBySourceId({});
      return undefined;
    }

    let cancelled = false;
    const loadRuns = async () => {
      const entries = await Promise.all(
        sources.map(async (sourceDoc) => {
          try {
            const latestRun = await fetchLatestIngestionRun(sourceDoc.id);
            return [sourceDoc.id, latestRun];
          } catch (runError) {
            console.error('Failed to load ingestion run for source', sourceDoc.id, runError);
            return [sourceDoc.id, null];
          }
        }),
      );
      if (!cancelled) {
        setLatestRunsBySourceId(Object.fromEntries(entries));
      }
    };

    loadRuns();
    return () => {
      cancelled = true;
      return undefined;
    };
  }, [sources]);

  const listEmptyMessage = useMemo(() => {
    if (sources.length === 0) {
      return 'No sources configured for this brewery.';
    }
    if (filteredSources.length === 0) {
      return 'No sources match the current filter.';
    }
    return undefined;
  }, [sources.length, filteredSources.length]);

  const handleOpenAddSource = useCallback(() => {
    setEditingSource(null);
    setFormDialogOpen(true);
  }, []);

  const handleCloseFormDialog = useCallback(() => {
    setFormDialogOpen(false);
    setEditingSource(null);
  }, []);

  const handleSourceStructureChange = useCallback(
    (event) => setSourceStructureFilter(event.target.value),
    [],
  );

  const handleSourceCategoryChange = useCallback(
    (event) => setSourceCategoryFilter(event.target.value),
    [],
  );

  const handleMonitoringStatusChange = useCallback(
    (event) => setMonitoringStatusFilter(event.target.value),
    [],
  );

  const handleEditButtonClick = useCallback(
    (event) => {
      const sourceId = event.currentTarget.getAttribute('data-source-id');
      const sourceDoc = sources.find((row) => row.id === sourceId);
      if (!sourceDoc) {
        return;
      }
      setEditingSource(sourceDoc);
      setFormDialogOpen(true);
    },
    [sources],
  );

  const handleMarkTrustedClick = useCallback(
    (event) => {
      const sourceId = event.currentTarget.getAttribute('data-source-id');
      if (sourceId) {
        markTrusted(sourceId);
      }
    },
    [markTrusted],
  );

  const handleStatusClick = useCallback(
    (event) => {
      const sourceId = event.currentTarget.getAttribute('data-source-id');
      const monitoringStatus = event.currentTarget.getAttribute('data-monitoring-status');
      if (sourceId && monitoringStatus) {
        updateStatus(sourceId, monitoringStatus);
      }
    },
    [updateStatus],
  );

  const handleScrapeClick = useCallback(
    (event) => {
      const sourceUrl = event.currentTarget.getAttribute('data-source-url');
      const sourceId = event.currentTarget.getAttribute('data-source-id');
      if (!sourceUrl || !sourceId) {
        return;
      }
      setScrapingSourceId(sourceId);
      const opened = openFindBeerWithUrl(sourceUrl);
      if (!opened) {
        setScrapingSourceId(null);
        return;
      }
      setScrapingSourceId(null);
    },
    [openFindBeerWithUrl],
  );

  const handleCheckNowClick = useCallback(
    (event) => {
      const sourceId = event.currentTarget.getAttribute('data-source-id');
      if (sourceId) {
        Promise.resolve(checkNow(sourceId)).catch(() => undefined);
      }
    },
    [checkNow],
  );

  const handleRecreateMonitorClick = useCallback(
    (event) => {
      const sourceId = event.currentTarget.getAttribute('data-source-id');
      const sourceDoc = sources.find((row) => row.id === sourceId);
      if (sourceDoc) {
        setRecreateSource(sourceDoc);
      }
    },
    [sources],
  );

  const handleCloseRecreate = useCallback(() => {
    setRecreateSource(null);
  }, []);

  const handleRecreateConfirm = useCallback(async () => {
    const sourceId = recreateSource?.id;
    if (!sourceId) {
      return;
    }
    try {
      await recreateMonitor(sourceId);
      setRecreateSource(null);
    } catch {
      // actionError is surfaced by useBreweryBeerSources
    }
  }, [recreateMonitor, recreateSource]);

  return (
    <Box sx={{ p: 2 }}>
      <StickyButtonBar>
        <Button variant="contained" color="primary" onClick={handleFindBeer}>
          Find Beer
        </Button>
        <Button variant="contained" color="primary" onClick={handleOpenAddSource}>
          Add Source
        </Button>
      </StickyButtonBar>
      <SourcesFilterBar
        sourceStructureFilter={sourceStructureFilter}
        sourceCategoryFilter={sourceCategoryFilter}
        monitoringStatusFilter={monitoringStatusFilter}
        onSourceStructureChange={handleSourceStructureChange}
        onSourceCategoryChange={handleSourceCategoryChange}
        onMonitoringStatusChange={handleMonitoringStatusChange}
      />
      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      <BeerSourceListPanel
        sources={filteredSources}
        loading={loading}
        error={error}
        latestRunsBySourceId={latestRunsBySourceId}
        actionLoading={actionLoading}
        pendingAction={pendingAction}
        scrapingSourceId={scrapingSourceId}
        emptyMessage={listEmptyMessage}
        onEditButtonClick={handleEditButtonClick}
        onMarkTrustedClick={handleMarkTrustedClick}
        onStatusClick={handleStatusClick}
        onScrapeClick={handleScrapeClick}
        onCheckNowClick={handleCheckNowClick}
        onRecreateMonitorClick={handleRecreateMonitorClick}
      />
      <BreweryBeerSourceFormDialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        breweryId={parentBreweryId}
        editingSource={editingSource}
        actionError={actionError}
        actionLoading={actionLoading}
        onAddOrUpdateSource={addOrUpdateSource}
      />
      <RecreateMonitorDialog
        open={Boolean(recreateSource)}
        breweryName={recreateSource?.url || breweryName}
        actionError={actionError}
        actionLoading={actionLoading}
        onClose={handleCloseRecreate}
        onConfirm={handleRecreateConfirm}
      />
      {findBeerDialog}
    </Box>
  );
}

BreweryBeerSourceFilterSelect.propTypes = {
  filterId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  minWidth: PropTypes.number.isRequired,
  allLabel: PropTypes.string,
  extraOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  formatOptionLabel: PropTypes.func,
  onChange: PropTypes.func.isRequired,
};

SourcesFilterBar.propTypes = {
  sourceStructureFilter: PropTypes.string.isRequired,
  sourceCategoryFilter: PropTypes.string.isRequired,
  monitoringStatusFilter: PropTypes.string.isRequired,
  onSourceStructureChange: PropTypes.func.isRequired,
  onSourceCategoryChange: PropTypes.func.isRequired,
  onMonitoringStatusChange: PropTypes.func.isRequired,
};

BreweryBeerSourcesSection.propTypes = {
  parentBreweryId: PropTypes.string.isRequired,
  breweryName: PropTypes.string,
  breweryCity: PropTypes.string,
  breweryState: PropTypes.string,
};

export default BreweryBeerSourcesSection;
